import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core'
import moment from 'moment'
import conferenceSlice, { deleteConferenceAlongData, fetchConference } from 'store/conference'
import Wrapper from 'components/Wrapper'
import BasicTable from 'components/BasicTable'
import getTableOptions from 'utils/getTableOptions'
import ResourceNotFound from 'components/ResourceNotFound'
import { EditOutlined, DeleteOutlined } from '@material-ui/icons'
import ConfirmModal from 'components/ConfirmationModal'
import { toast } from 'utils/notify'
import { Roles } from 'constants/index'

const superAdminTableHead = [
  'Conf Name',
  'Code',
  'Start Date',
  'End Date',
  'Viewable Until',
  'Is Active',
  'Organizer',
  'Actions'
]

const defaultTableHead = [
  'Conf Name',
  'Code',
  'Start Date',
  'End Date',
  'Viewable Until',
  'Is Active',
  'Actions'
]

const formatTableData = (conferences, isSuperAdmin, organizers) =>
  conferences.map((conference) => {
    const organizer = organizers?.find((o) => o.uid === conference.organizerId)
    const row = [
      conference?.confName,
      conference?.confCode,
      moment(conference?.startDate).tz(conference?.timezone).format('M/D/YYYY'),
      moment(conference?.endDate).tz(conference?.timezone).format('M/D/YYYY'),
      conference?.viewableUntil
        ? moment(conference.viewableUntil).tz(conference?.timezone).format('M/D/YYYY')
        : '—',
      conference?.isActive ? 'Yes' : 'No'
    ]
    if (isSuperAdmin) {
      row.push(
        organizer ? (
          <Chip
            key={conference.id}
            label={organizer.fullName || organizer.email}
            size="small"
            style={{ backgroundColor: '#d1fae5', color: '#065f46' }}
          />
        ) : (
          '—'
        )
      )
    }
    row.push(<ActionButtons key={conference.id} conference={conference} />)
    return row
  })

const ActionButtons = ({ conference }) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const location = useLocation()
  const history = useHistory()
  const [deletedId, setDeletedId] = useState('')
  const [loading, setLoading] = useState(false)

  const { user } = useSelector((state) => state.auth)
  const isOrganizer = user?.role === Roles.ORGANIZER
  const canDelete = !isOrganizer || conference?.organizerId === user?.id

  const openModal = (id) => {
    setDeletedId(id)
  }
  const closeModal = () => {
    setDeletedId('')
  }
  const handleEdit = () => {
    history.push(`${location.pathname}/edit/${conference?.id}`)
  }

  const handleDeleteConference = async () => {
    if (deletedId) {
      setLoading(true)
      await dispatch(deleteConferenceAlongData(deletedId))
      toast.success('Conference and all associated data deleted.')
    }
    setLoading(false)
  }
  return (
    <>
      <ConfirmModal
        dialogTitle="Delete Conference"
        dialogText="This will delete all the data related to this conference, Do you still wish to continue? "
        open={Boolean(deletedId)}
        closeModal={closeModal}
        handleConfirm={handleDeleteConference}
        inProgress={loading}
      />
      <Tooltip title="Edit">
        <IconButton className={classes.iconButton} onClick={handleEdit}>
          <EditOutlined color="action" />
        </IconButton>
      </Tooltip>

      {canDelete && (
        <Tooltip title="Delete">
          <IconButton className={classes.iconButton} onClick={() => openModal(conference?.id)}>
            <DeleteOutlined color="action" />
          </IconButton>
        </Tooltip>
      )}
    </>
  )
}

const ConferenceListing = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const [tab, setTab] = useState('active')

  const { user } = useSelector((state) => state.auth)
  const { inProgress, conferences, currentPage, pageSize } = useSelector(
    (state) => state.conference
  )
  const { organizers } = useSelector((state) => state.organizers)

  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN
  const isOrganizer = user?.role === Roles.ORGANIZER

  useEffect(() => {
    dispatch(fetchConference(isOrganizer ? user?.id : null))
  }, [dispatch, isOrganizer, user?.id])

  const options = getTableOptions()

  const today = moment()
  const activeConferences = conferences.filter(
    (c) => !(c.isArchived && c.viewableUntil && moment(c.viewableUntil).isBefore(today, 'day'))
  )
  const archivedConferences = conferences.filter(
    (c) => c.isArchived && c.viewableUntil && moment(c.viewableUntil).isBefore(today, 'day')
  )

  const visibleConferences = tab === 'active' ? activeConferences : archivedConferences

  return (
    <>
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <h2>Conferences</h2>
        <Button
          variant="contained"
          color="primary"
          onClick={() => history.push(`${location.pathname}/new`)}>
          New Conference
        </Button>
      </div>
      <Paper className={classes.tabsContainer}>
        <Tabs
          value={tab}
          onChange={(_, val) => setTab(val)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}>
          <Tab
            label={
              <span>
                {'Active '}
                <Chip size="small" label={activeConferences.length} className={classes.countChip} />
              </span>
            }
            value="active"
          />
          <Tab
            label={
              <span>
                {'Archived '}
                <Chip
                  size="small"
                  label={archivedConferences.length}
                  className={classes.countChip}
                />
              </span>
            }
            value="archived"
          />
        </Tabs>
      </Paper>
      {tab === 'archived' && (
        <Typography variant="body2" className={classes.archivedNote}>
          Conferences are archived when their <strong>Viewable Until</strong> date has passed.
        </Typography>
      )}
      <Wrapper inProgress={inProgress}>
        <Paper variant="outlined" style={{ marginTop: '16px' }}>
          {visibleConferences.length ? (
            <BasicTable
              data={formatTableData(visibleConferences, isSuperAdmin, organizers)}
              columns={isSuperAdmin ? superAdminTableHead : defaultTableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(conferenceSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(conferenceSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          ) : (
            <ResourceNotFound
              message={tab === 'active' ? 'No active conferences' : 'No archived conferences'}
            />
          )}
        </Paper>
      </Wrapper>
    </>
  )
}

export default ConferenceListing

const useStyles = makeStyles((theme) => ({
  iconButton: {
    padding: '8px'
  },
  tabsContainer: {
    borderRadius: 0,
    boxShadow: 'none'
  },
  tabs: {
    '& .MuiTab-root.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: 'rgba(51,220,235,0.16)'
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.primary.main,
      height: '2px'
    }
  },
  countChip: {
    height: '18px',
    fontSize: '11px',
    marginLeft: '4px',
    pointerEvents: 'none'
  },
  archivedNote: {
    marginTop: '8px',
    color: theme.palette.text.secondary
  }
}))
